export const en = {
  menu: {
    root: 'Inventory',
    levels: 'Inventory Levels',
    lots: 'Inventory Lots',
    tags: 'Inventory Tags',
    periods: 'Inventory Periods',
    locations: 'Locations',
    zones: 'Zones',
    cycleCountSchedules: 'Count Schedules',
    counts: 'Cycle Counts',
  },
  inventoryLevelsView: {
    title: 'Inventory Levels',
    subtitle: 'Track current inventory quantities by item and facility',
    viewModes: {
      ourMaterial: 'Our Material',
      tollingMaterial: 'Tolling Material',
    },
    filters: {
      search: 'Search items...',
      allFacilities: 'All Facilities',
      allLocations: 'All Locations',
      ourFacilities: 'Our Facilities',
      externalProcessors: 'External Processors',
      allZones: 'All Zones',
      allQuantities: 'All quantities',
      hideZeroQuantity: 'Hide zero quantity',
      negativeOnly: 'Negative only',
    },
    summary: {
      totalItems: 'Total Items',
      itemsWithStock: 'Items With Stock',
      totalQuantity: 'Total Quantity',
    },
    table: {
      aria: 'Inventory Levels List',
      noFacility: 'No Facility',
      columns: {
        item: 'Item',
        facility: 'Facility',
        location: 'Location',
        owner: 'Owner',
        quantity: 'Quantity',
        totalValue: 'Total Value',
        wac: 'WAC',
      },
    },
    content: {
      error: (message: string) => `Error loading inventory levels: ${message}`,
    },
    groups: {
      ungrouped: 'Ungrouped',
      items: (count: number) => `${count} item${count !== 1 ? 's' : ''}`,
    },
    emptyState: {
      title: 'No inventory found',
      subtitle:
        'No inventory levels match your current filters. Try adjusting your search criteria.',
    },
  },
  movementHistoryView: {
    title: 'Movement History',
    subtitle: (itemName: string) => `Inventory movements for ${itemName}`,
    inventoryDetails: 'Inventory details',
    backToLevels: 'Back to Inventory Levels',
    tabs: {
      facilities: 'Facilities',
      history: 'History',
    },
    facilityBreakdown: {
      facility: 'Facility',
      quantity: 'Quantity',
      totalValue: 'Total Value',
      wac: 'WAC',
    },
    facilitySelector: 'Facility',
    filters: {
      allFacilities: 'All Facilities',
    },
    table: {
      aria: 'Inventory Movement History',
      columns: {
        date: 'Date',
        type: 'Type',
        transaction: 'Transaction',
        quantity: 'Quantity',
        cost: 'Cost',
        status: 'Status',
        originator: 'Origin',
        runningQuantity: 'Running Qty',
        runningValue: 'Running Value',
        runningWac: 'Running WAC',
      },
    },
    movementTypes: {
      In: 'In',
      Out: 'Out',
      Internal: 'Internal',
      Reset: 'Reset',
    },
    transactionTypes: {
      Purchase: 'Purchase',
      Sale: 'Sale',
      Transfer: 'Transfer',
      Tagging: 'Tagging',
      Untagging: 'Untagging',
      Processing: 'Processing',
      Upgrade: 'Upgrade',
      Downgrade: 'Downgrade',
      Accounting: 'Accounting',
      BalanceAdjustment: 'Balance Adjustment',
      PhysicalInventory: 'Physical Inventory',
      PurchaseReversion: 'Purchase Reversion',
      SaleReversion: 'Sale Reversion',
      TransferReversion: 'Transfer Reversion',
    },
    statusTypes: {
      Pending: 'Pending',
      InTransit: 'In Transit',
      Completed: 'Completed',
      Canceled: 'Canceled',
    },
    content: {
      error: (message: string) => `Error loading movement history: ${message}`,
    },
    emptyState: {
      title: 'No movements found',
      subtitle: 'This item has no inventory movements recorded.',
    },
    actions: {
      allocateToLot: 'Allocate to Lot',
    },
  },
  lotsListView: {
    title: 'Inventory Lots',
    subtitle: 'Manage discrete inventory lots by facility',
    createLot: 'Create Lot',
    filters: {
      search: 'Search lots...',
      allFacilities: 'All Facilities',
      allStatuses: 'All Statuses',
    },
    summary: {
      totalLots: 'Total Lots',
      activeLots: 'Active Lots',
      closedLots: 'Closed Lots',
    },
    table: {
      aria: 'Inventory Lots List',
      columns: {
        lotNumber: 'Lot Number',
        facility: 'Facility',
        status: 'Status',
        quantity: 'Quantity',
        items: 'Items',
        createdAt: 'Created',
      },
      moreItems: (count: number) => `+${count} more`,
      emptyLot: '—',
    },
    statusTypes: {
      Active: 'Active',
      Closed: 'Closed',
      Merged: 'Merged',
    },
    content: {
      error: (message: string) => `Error loading inventory lots: ${message}`,
    },
    emptyState: {
      title: 'No lots found',
      subtitle:
        'No inventory lots match your current filters. Try adjusting your search criteria or create a new lot.',
    },
    dialog: {
      createTitle: 'Create Inventory Lot',
      editTitle: 'Edit Inventory Lot',
      lotNumberLabel: 'Lot Number',
      lotNumberPlaceholder: 'Enter lot number...',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select a facility...',
      locationLabel: 'Location',
      locationPlaceholder: 'Select a location...',
      noLocation: 'No Location',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Optional description...',
      statusLabel: 'Status',
      cancel: 'Cancel',
      create: 'Create Lot',
      creating: 'Creating...',
      update: 'Update Lot',
      updating: 'Updating...',
      createSuccess: 'Lot created successfully',
      updateSuccess: 'Lot updated successfully',
      createError: (message: string) => `Failed to create lot: ${message}`,
      updateError: (message: string) => `Failed to update lot: ${message}`,
      unknownError: 'Unknown error',
      validation: {
        lotNumberRequired: 'Lot number is required',
        facilityRequired: 'Facility is required',
      },
    },
    allocation: {
      allocateToLot: 'Allocate to Lot',
      addMovements: 'Add Movements',
      selectLot: 'Select a lot',
      quantity: 'Quantity',
      maxAvailable: (max: number, unit: string) => `Max: ${max} ${unit}`,
      success: 'Successfully allocated to lot',
      error: 'Failed to allocate to lot',
      noEligibleMovements: 'No eligible movements found',
      noMovementsSelected: 'No movements selected',
      movementsToAllocate: 'Movements to Allocate',
      allocate: 'Allocate',
      searchMovements: 'Search Movements',
      searchPlaceholder: 'Search by item name or movement number...',
      loadingMovements: 'Loading movements...',
      errorLoadingMovements: 'Failed to load movements. Please try again.',
      selectedMovements: (count: number) => `Selected Movements (${count})`,
      addToLot: 'Add to Lot',
    },
    detail: {
      title: (lotNumber: string) => `Lot ${lotNumber}`,
      backToLots: 'Back to Lots',
      allocations: 'Allocations',
      noAllocations: 'No movements allocated to this lot',
      addMovements: 'Add Movements',
      edit: 'Edit',
      closeLot: 'Close Lot',
      deallocate: 'Deallocate',
      deallocateSuccess: 'Successfully deallocated movements',
      deallocateError: 'Failed to deallocate movements',
      deallocateConfirm: (count: number) =>
        `Deallocate ${count} allocation${count > 1 ? 's' : ''}?`,
      deallocateConfirmDescription:
        'This will remove the selected allocations from this lot. The movements will become unallocated.',
      toolbar: {
        selected: 'selected',
        deallocate: 'Deallocate',
      },
      table: {
        aria: 'Lot Allocations',
        columns: {
          source: 'Source',
          type: 'Type',
          item: 'Item',
          quantity: 'Quantity',
          date: 'Allocated',
        },
        tagged: 'Tagged',
        loose: 'Loose',
      },
      summary: {
        title: 'Summary',
        totalItems: 'Items',
        totalQuantity: 'Total Quantity',
      },
    },
  },
  locationsListView: {
    title: 'Inventory Locations',
    subtitle: 'Manage storage locations within facilities',
    createLocation: 'Create Location',
    filters: {
      search: 'Search locations...',
      allFacilities: 'All Facilities',
      allTypes: 'All Types',
      allStatuses: 'All Statuses',
    },
    summary: {
      totalLocations: 'Total Locations',
      activeLocations: 'Active',
      closedLocations: 'Closed',
      archivedLocations: 'Archived',
    },
    table: {
      aria: 'Inventory Locations List',
      columns: {
        locationCode: 'Location Code',
        facility: 'Facility',
        parentLocation: 'Parent',
        locationType: 'Type',
        status: 'Status',
        capacity: 'Capacity',
        createdAt: 'Created',
      },
    },
    locationTypes: {
      Zone: 'Zone',
      Aisle: 'Aisle',
      Shelf: 'Shelf',
      Bin: 'Bin',
      Other: 'Other',
    },
    statusTypes: {
      Active: 'Active',
      Closed: 'Closed',
      Archived: 'Archived',
    },
    content: {
      error: (message: string) =>
        `Error loading inventory locations: ${message}`,
    },
    emptyState: {
      title: 'No locations found',
      subtitle:
        'No inventory locations match your current filters. Try adjusting your search criteria or create a new location.',
    },
    actions: {
      edit: 'Edit',
      close: 'Close Location',
      archive: 'Archive Location',
      closeSuccess: 'Location closed successfully',
      closeError: (message: string) => `Failed to close location: ${message}`,
      archiveSuccess: 'Location archived successfully',
      archiveError: (message: string) =>
        `Failed to archive location: ${message}`,
    },
    confirmClose: {
      title: 'Close Location',
      description: (code: string) =>
        `Are you sure you want to close location "${code}"? Closed locations cannot receive new inventory.`,
      confirm: 'Close Location',
      cancel: 'Cancel',
    },
    confirmArchive: {
      title: 'Archive Location',
      description: (code: string) =>
        `Are you sure you want to archive location "${code}"? Archived locations will be hidden from the main list.`,
      confirm: 'Archive Location',
      cancel: 'Cancel',
    },
    dialog: {
      createTitle: 'Create Inventory Location',
      editTitle: 'Edit Inventory Location',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select a facility...',
      parentLocationLabel: 'Parent Location',
      parentLocationPlaceholder: 'Select parent location...',
      noParent: 'No parent (top-level)',
      locationCodeLabel: 'Location Code',
      locationCodePlaceholder: 'Enter location code...',
      nameLabel: 'Name',
      namePlaceholder: 'Enter location name...',
      locationTypeLabel: 'Location Type',
      capacityValueLabel: 'Capacity',
      capacityValuePlaceholder: 'Enter capacity...',
      capacityUnitLabel: 'Unit',
      capacityUnitPlaceholder: 'e.g., pallets',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Optional description...',
      cancel: 'Cancel',
      create: 'Create Location',
      creating: 'Creating...',
      update: 'Update Location',
      updating: 'Updating...',
      createSuccess: 'Location created successfully',
      updateSuccess: 'Location updated successfully',
      createError: (message: string) => `Failed to create location: ${message}`,
      updateError: (message: string) => `Failed to update location: ${message}`,
      unknownError: 'Unknown error',
      validation: {
        locationCodeRequired: 'Location code is required',
        nameRequired: 'Name is required',
        facilityRequired: 'Facility is required',
      },
    },
  },
  zonesListView: {
    title: 'Inventory Zones',
    subtitle: 'Manage storage zones within facilities',
    createZone: 'Create Zone',
    filters: {
      search: 'Search zones...',
      allFacilities: 'All Facilities',
      allTypes: 'All Types',
      allStatuses: 'All Statuses',
    },
    summary: {
      totalZones: 'Total Zones',
      activeZones: 'Active',
      closedZones: 'Closed',
      archivedZones: 'Archived',
    },
    table: {
      aria: 'Inventory Zones List',
      columns: {
        zoneCode: 'Zone Code',
        facility: 'Facility',
        parentZone: 'Parent',
        zoneType: 'Type',
        status: 'Status',
        capacity: 'Capacity',
        createdAt: 'Created',
      },
    },
    zoneTypes: {
      Zone: 'Zone',
      Aisle: 'Aisle',
      Shelf: 'Shelf',
      Bin: 'Bin',
      Other: 'Other',
    },
    statusTypes: {
      Active: 'Active',
      Closed: 'Closed',
      Archived: 'Archived',
    },
    content: {
      error: (message: string) => `Error loading inventory zones: ${message}`,
    },
    emptyState: {
      title: 'No zones found',
      subtitle:
        'No inventory zones match your current filters. Try adjusting your search criteria or create a new zone.',
    },
    actions: {
      edit: 'Edit',
      close: 'Close Zone',
      archive: 'Archive Zone',
      closeSuccess: 'Zone closed successfully',
      closeError: (message: string) => `Failed to close zone: ${message}`,
      archiveSuccess: 'Zone archived successfully',
      archiveError: (message: string) => `Failed to archive zone: ${message}`,
    },
    confirmClose: {
      title: 'Close Zone',
      description: (code: string) =>
        `Are you sure you want to close zone "${code}"? Closed zones cannot receive new inventory.`,
      confirm: 'Close Zone',
      cancel: 'Cancel',
    },
    confirmArchive: {
      title: 'Archive Zone',
      description: (code: string) =>
        `Are you sure you want to archive zone "${code}"? Archived zones will be hidden from the main list.`,
      confirm: 'Archive Zone',
      cancel: 'Cancel',
    },
    dialog: {
      createTitle: 'Create Inventory Zone',
      editTitle: 'Edit Inventory Zone',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select a facility...',
      parentZoneLabel: 'Parent Zone',
      parentZonePlaceholder: 'Select parent zone...',
      noParent: 'No parent (top-level)',
      zoneCodeLabel: 'Zone Code',
      zoneCodePlaceholder: 'Enter zone code...',
      nameLabel: 'Name',
      namePlaceholder: 'Enter zone name...',
      zoneTypeLabel: 'Zone Type',
      capacityValueLabel: 'Capacity',
      capacityValuePlaceholder: 'Enter capacity...',
      capacityUnitLabel: 'Unit',
      capacityUnitPlaceholder: 'e.g., pallets',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Optional description...',
      cancel: 'Cancel',
      create: 'Create Zone',
      creating: 'Creating...',
      update: 'Update Zone',
      updating: 'Updating...',
      createSuccess: 'Zone created successfully',
      updateSuccess: 'Zone updated successfully',
      createError: (message: string) => `Failed to create zone: ${message}`,
      updateError: (message: string) => `Failed to update zone: ${message}`,
      unknownError: 'Unknown error',
      validation: {
        zoneCodeRequired: 'Zone code is required',
        nameRequired: 'Name is required',
        facilityRequired: 'Facility is required',
      },
    },
  },
  periodsListView: {
    title: 'Inventory Periods',
    subtitle: 'Manage inventory accounting periods by facility',
    filters: {
      allFacilities: 'All Facilities',
      allStatuses: 'All Statuses',
    },
    table: {
      aria: 'Inventory Periods List',
      columns: {
        name: 'Name',
        facility: 'Facility',
        status: 'Status',
        startDate: 'Start Date',
        endDate: 'End Date',
        lockedAt: 'Locked At',
      },
    },
    statusTypes: {
      Open: 'Open',
      Closed: 'Closed',
      Locked: 'Locked',
      Archived: 'Archived',
    } as Record<string, string>,
    content: {
      error: (message: string) => `Error loading inventory periods: ${message}`,
    },
    emptyState: {
      title: 'No periods found',
      subtitle:
        'No inventory periods match your current filters. Try adjusting your search criteria.',
    },
    actions: {
      lock: 'Lock Period',
      unlock: 'Unlock Period',
      archive: 'Archive Period',
      lockSuccess: 'Period locked successfully',
      lockError: (message: string) => `Failed to lock period: ${message}`,
      unlockSuccess: 'Period unlocked successfully',
      unlockError: (message: string) => `Failed to unlock period: ${message}`,
      archiveSuccess: 'Period archived successfully',
      archiveError: (message: string) => `Failed to archive period: ${message}`,
      unknownError: 'Unknown error',
    },
    lockDialog: {
      title: 'Lock Period',
      description: (name: string) =>
        `Review the checklist before locking period "${name}".`,
      checklistTitle: 'Pre-lock Checklist',
      blockingWarning:
        'Blocking items must be resolved before this period can be locked.',
      confirm: 'Lock Period',
      cancel: 'Cancel',
    },
    unlockDialog: {
      title: 'Unlock Period',
      description: (name: string) =>
        `Temporarily unlock period "${name}" for corrections.`,
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Enter the reason for unlocking this period...',
      durationLabel: 'Auto-Relock Duration',
      confirm: 'Unlock Period',
      cancel: 'Cancel',
      validation: {
        reasonRequired: 'A reason is required to unlock the period',
      },
    },
    detail: {
      title: (name: string) => `Period: ${name}`,
      backToPeriods: 'Back to Periods',
      overview: 'Overview',
      periodInfo: 'Period Information',
      autoCloseInfo: (endDate: string) =>
        `This period will close automatically on ${endDate}.`,
      autoRelockInfo: (expiresAt: string) =>
        `This period will automatically re-lock on ${expiresAt}.`,
      fields: {
        name: 'Name',
        facility: 'Facility',
        periodType: 'Period Type',
        startDate: 'Start Date',
        endDate: 'End Date',
        status: 'Status',
        closedAt: 'Closed At',
        lockedAt: 'Locked At',
        unlockReason: 'Unlock Reason',
        notes: 'Notes',
        createdAt: 'Created',
        updatedAt: 'Updated',
      },
      balanceSnapshot: 'Balance Snapshot',
      balanceTable: {
        aria: 'Period Balance Snapshot',
        empty: 'No balance data available',
        columns: {
          item: 'Item',
          account: 'Account',
          quantity: 'Quantity',
          wacPerUnit: 'WAC/Unit',
          totalValue: 'Total Value',
        },
      },
    },
    periodConfig: {
      configureButton: 'Configure',
      createTitle: 'Configure Inventory Periods',
      editTitle: 'Edit Period Configuration',
      sections: {
        periodSettings: 'Period Settings',
        straddleSettings: 'Straddle Settings',
      },
      fields: {
        periodType: 'Period Type',
        fiscalYearStartMonth: 'Fiscal Year Start Month',
        allowStraddleCompletion: 'Allow Straddle Completion',
        straddleWindowPeriods: 'Straddle Window (Periods)',
        requireStraddleApproval: 'Require Straddle Approval',
      },
      periodTypeOptions: {
        CalendarMonth: 'Calendar Month',
        FiscalPeriod: 'Fiscal Period',
      } as Record<string, string>,
      months: {
        1: 'January',
        2: 'February',
        3: 'March',
        4: 'April',
        5: 'May',
        6: 'June',
        7: 'July',
        8: 'August',
        9: 'September',
        10: 'October',
        11: 'November',
        12: 'December',
      } as Record<number, string>,
      actions: {
        save: 'Save',
        cancel: 'Cancel',
      },
      messages: {
        createSuccess: 'Period configuration created successfully',
        updateSuccess: 'Period configuration updated successfully',
        createError: (message: string) =>
          `Failed to create period configuration: ${message}`,
        updateError: (message: string) =>
          `Failed to update period configuration: ${message}`,
      },
      validation: {
        fiscalYearStartMonthRequired:
          'Fiscal year start month is required for fiscal periods',
      },
    },
  },
  tagsListView: {
    title: 'Inventory Tags',
    subtitle: 'Manage inventory tags for tracking materials',
    createTag: 'Create Tag',
    filters: {
      search: 'Search tags...',
      allFacilities: 'All Facilities',
      allTypes: 'All Types',
      allStatuses: 'All Statuses',
    },
    summary: {
      totalTags: 'Total Tags',
      activeTags: 'Active',
      draftTags: 'Draft',
      inInventoryTags: 'In Inventory',
    },
    table: {
      aria: 'Inventory Tags List',
      columns: {
        tagNumber: 'Tag Number',
        facility: 'Facility',
        tagType: 'Type',
        status: 'Status',
        netQuantity: 'Net Qty',
        generatedAt: 'Generated',
        lot: 'Lot',
      },
    },
    tagTypes: {
      WorkInProgress: 'Work In Progress',
      FinishedGood: 'Finished Good',
    },
    statusTypes: {
      Active: 'Active',
      Revised: 'Revised',
      Cancelled: 'Cancelled',
      Confirmed: 'Confirmed',
      Draft: 'Draft',
      InInventory: 'In Inventory',
      InTransfer: 'In Transfer',
      Loaded: 'Loaded',
      PendingPurchase: 'Pending Purchase',
      Reserved: 'Reserved',
      Shipped: 'Shipped',
      Sold: 'Sold',
      Transferred: 'Transferred',
      Voided: 'Voided',
    },
    captureMethods: {
      Automatic: 'Automatic',
      Manual: 'Manual',
    },
    content: {
      error: (message: string) => `Error loading inventory tags: ${message}`,
    },
    emptyState: {
      title: 'No tags found',
      subtitle:
        'No inventory tags match your current filters. Try adjusting your search criteria or create a new tag.',
    },
    actions: {
      edit: 'Edit',
      delete: 'Delete Tag',
      deleteSuccess: 'Tag deleted successfully',
      deleteError: (message: string) => `Failed to delete tag: ${message}`,
    },
    confirmDelete: {
      title: 'Delete Tag',
      description: (tagNumber: string) =>
        `Are you sure you want to delete tag "${tagNumber}"? This action cannot be undone.`,
      confirm: 'Delete Tag',
      cancel: 'Cancel',
    },
    dialog: {
      createTitle: 'Create Inventory Tag',
      editTitle: 'Edit Inventory Tag',
      tagNumberLabel: 'Tag Number',
      tagNumberPlaceholder: 'Leave empty for auto-generated...',
      tagTypeLabel: 'Tag Type',
      statusLabel: 'Status',
      itemLabel: 'Item',
      itemPlaceholder: 'Select an item...',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select a facility...',
      lotLabel: 'Inventory Lot',
      lotPlaceholder: 'Select lot (optional)',
      generatedAtLabel: 'Generated At',
      grossQuantityLabel: 'Gross',
      tareLabel: 'Tare',
      netQuantityLabel: 'Net',
      quantityCaptureMethodLabel: 'Quantity Capture Method',
      tareCaptureMethodLabel: 'Tare Capture Method',
      isRevisedLabel: 'Is Revised',
      cancel: 'Cancel',
      create: 'Create Tag',
      creating: 'Creating...',
      update: 'Update Tag',
      updating: 'Updating...',
      createSuccess: 'Tag created successfully',
      updateSuccess: 'Tag updated successfully',
      createError: (message: string) => `Failed to create tag: ${message}`,
      updateError: (message: string) => `Failed to update tag: ${message}`,
      unknownError: 'Unknown error',
      validation: {
        tagTypeRequired: 'Tag type is required',
        statusRequired: 'Status is required',
        itemRequired: 'Item is required',
        facilityRequired: 'Facility is required',
        generatedAtRequired: 'Generated date is required',
        grossQuantityRequired: 'Gross quantity is required',
        tareRequired: 'Tare is required',
      },
    },
    detail: {
      title: (tagNumber: string) => `Tag ${tagNumber}`,
      backToTags: 'Back to Tags',
      edit: 'Edit',
      allocateMovements: 'Allocate Movements',
      deallocate: 'Deallocate',
      lot: 'Lot',
      netQuantity: 'Net Quantity',
      quantitySummary: 'Quantity Summary',
      inQuantity: 'In',
      outQuantity: 'Out',
      netFromAllocations: 'Net (Allocations)',
      allocations: 'Movement Allocations',
      noAllocations: 'No movements allocated to this tag',
      table: {
        aria: 'Tag Allocations',
        columns: {
          source: 'Source',
          item: 'Item',
          quantity: 'Quantity',
          date: 'Allocated',
        },
      },
      allocation: {
        title: 'Allocate Movements to Tag',
        searchMovements: 'Search Movements',
        searchPlaceholder: 'Search by item name or movement number...',
        loadingMovements: 'Loading movements...',
        noEligibleMovements: 'No eligible movements found',
        selectedMovements: (count: number) => `Selected Movements (${count})`,
        quantity: 'Quantity',
        maxAvailable: (max: number, unit: string) => `Max: ${max} ${unit}`,
        allocate: 'Allocate Selected',
        success: 'Successfully allocated to tag',
        error: 'Failed to allocate to tag',
      },
    },
  },
  cycleCountSchedulesView: {
    title: 'Cycle Count Schedules',
    subtitle: 'Manage scheduled cycle count configurations',
    createSchedule: 'Create Schedule',
    filters: {
      allFacilities: 'All Facilities',
    },
    summary: {
      totalSchedules: 'Total Schedules',
      activeSchedules: 'Active',
      inactiveSchedules: 'Inactive',
    },
    table: {
      aria: 'Cycle Count Schedules List',
      columns: {
        name: 'Name',
        facility: 'Facility',
        frequency: 'Frequency',
        scopeType: 'Scope',
        isActive: 'Status',
        lastGeneratedAt: 'Last Generated',
      },
    },
    content: {
      error: (message: string) =>
        `Error loading cycle count schedules: ${message}`,
    },
    emptyState: {
      title: 'No schedules found',
      subtitle:
        'No cycle count schedules match your current filters. Try adjusting your search criteria or create a new schedule.',
    },
    actions: {
      edit: 'Edit',
      activate: 'Activate',
      deactivate: 'Deactivate',
      delete: 'Delete',
      activateSuccess: 'Schedule activated successfully',
      deactivateSuccess: 'Schedule deactivated successfully',
      toggleError: (message: string) => `Failed to toggle schedule: ${message}`,
      deleteSuccess: 'Schedule deleted successfully',
      deleteError: (message: string) => `Failed to delete schedule: ${message}`,
    },
    confirmDelete: {
      title: 'Delete Schedule',
      description: (name: string) =>
        `Are you sure you want to delete schedule "${name}"? This action cannot be undone.`,
      confirm: 'Delete Schedule',
      cancel: 'Cancel',
    },
    dialog: {
      createTitle: 'Create Cycle Count Schedule',
      editTitle: 'Edit Cycle Count Schedule',
      facilityLabel: 'Facility',
      facilityPlaceholder: 'Select a facility...',
      nameLabel: 'Name',
      namePlaceholder: 'Enter schedule name...',
      frequencyLabel: 'Frequency',
      scopeTypeLabel: 'Scope Type',
      toleranceLabel: 'Default Tolerance (%)',
      blindCountLabel: 'Blind Count',
      cancel: 'Cancel',
      create: 'Create Schedule',
      creating: 'Creating...',
      update: 'Update Schedule',
      updating: 'Updating...',
      createSuccess: 'Schedule created successfully',
      updateSuccess: 'Schedule updated successfully',
      createError: (message: string) => `Failed to create schedule: ${message}`,
      updateError: (message: string) => `Failed to update schedule: ${message}`,
      unknownError: 'Unknown error',
      validation: {
        nameRequired: 'Name is required',
        facilityRequired: 'Facility is required',
      },
    },
  },
  cycleCountTasksView: {
    title: 'Cycle Count Tasks',
    subtitle: 'Manage and execute inventory count tasks',
    filters: {
      search: 'Search tasks...',
      allFacilities: 'All Facilities',
      allStatuses: 'All Statuses',
      allCountTypes: 'All Count Types',
    },
    table: {
      aria: 'Cycle Count Tasks List',
      columns: {
        taskNumber: 'Task Number',
        facility: 'Facility',
        countType: 'Count Type',
        status: 'Status',
        assignedTo: 'Assigned To',
        dueDate: 'Due Date',
      },
    },
    statusTypes: {
      Pending: 'Pending',
      InProgress: 'In Progress',
      Completed: 'Completed',
      Approved: 'Approved',
      Cancelled: 'Cancelled',
    } as Record<string, string>,
    countTypes: {
      CycleCount: 'Cycle Count',
      PhysicalInventory: 'Physical Inventory',
      SpotCheck: 'Spot Check',
    } as Record<string, string>,
    content: {
      error: (message: string) => `Error loading cycle count tasks: ${message}`,
    },
    createTask: {
      button: 'Create Task',
      dialogTitle: 'Create Cycle Count Task',
      facility: 'Facility',
      countType: 'Count Type',
      dueDate: 'Due Date',
      assignedTo: 'Assigned To',
      blindCount: 'Blind Count',
      tolerancePercentage: 'Tolerance %',
      notes: 'Notes',
      notesPlaceholder: 'Optional notes...',
      submit: 'Create',
      cancel: 'Cancel',
      success: 'Cycle count task created',
      error: (message: string) => `Failed to create task: ${message}`,
      countTypes: {
        Full: 'Full',
        Category: 'Category',
        Zone: 'Zone',
        Abc: 'ABC',
        Value: 'Value',
      } as Record<string, string>,
    },
    emptyState: {
      title: 'No tasks found',
      subtitle:
        'No cycle count tasks match your current filters. Try adjusting your search criteria.',
    },
    detail: {
      title: (taskNumber: string) => `Count Task ${taskNumber}`,
      backToTasks: 'Back to Tasks',
      linesTab: 'Count Lines',
      noLines: 'No count lines for this task',
      hidden: 'Hidden',
      countedQuantity: 'Counted Quantity',
      info: {
        countType: 'Count Type',
        assignedTo: 'Assigned To',
        dueDate: 'Due Date',
        blindCount: 'Blind Count',
        yes: 'Yes',
        no: 'No',
        startedAt: 'Started',
        completedAt: 'Completed',
        approvedAt: 'Approved',
        approvedBy: 'Approved By',
      },
      linesTable: {
        item: 'Item',
        account: 'Account',
        expected: 'Expected Qty',
        counted: 'Counted Qty',
        variance: 'Variance',
        tolerance: 'Tolerance',
        adjustmentStatus: 'Adj. Status',
        actions: 'Actions',
      },
      actions: {
        start: 'Start Count',
        complete: 'Complete Count',
        approve: 'Approve',
        cancel: 'Cancel',
        startSuccess: 'Count task started',
        completeSuccess: 'Count task completed',
        approveSuccess: 'Count task approved',
        cancelSuccess: 'Count task cancelled',
        startError: (message: string) =>
          `Failed to start count task: ${message}`,
        completeError: (message: string) =>
          `Failed to complete count task: ${message}`,
        approveError: (message: string) =>
          `Failed to approve count task: ${message}`,
        approveUnresolvedLinesError:
          'All lines requiring approval must be approved or rejected before the task can be approved.',
        cancelError: (message: string) =>
          `Failed to cancel count task: ${message}`,
        reopen: 'Reopen',
        reopenSuccess: 'Count task reopened',
        reopenError: (message: string) =>
          `Failed to reopen count task: ${message}`,
        unknownError: 'Unknown error',
      },
      saveCounts: 'Save Counts',
      saveCountsSuccess: 'Counts saved successfully',
      saveCountsError: (message: string) => `Failed to save counts: ${message}`,
      saveCountsPartialError: (succeeded: number, failed: number) =>
        `${succeeded} count(s) saved, ${failed} failed. Please retry the failed entries.`,
      lineActions: {
        approve: 'Approve Adjustment',
        reject: 'Reject Adjustment',
        recount: 'Request Recount',
        recountRequested: 'Recount Requested',
        costOverride: 'Unit Cost Override',
        costOverridePlaceholder: 'WAC',
        approveSuccess: 'Adjustment approved',
        approveError: (message: string) =>
          `Failed to approve adjustment: ${message}`,
        rejectSuccess: 'Adjustment rejected',
        rejectError: (message: string) =>
          `Failed to reject adjustment: ${message}`,
        recountSuccess: 'Recount requested',
        recountError: (message: string) =>
          `Failed to request recount: ${message}`,
      },
    },
  },
  logs: {
    rawPayloadParseError: 'Raw Payload (Parse Error)',
    unknownEventType: 'Unknown Event Type',
    unknownEvent: 'Unknown Event',
    noDataAvailable: 'No data available',
    events: {
      categoryCreated: 'Category Created',
      categoryUpdated: 'Category Updated',
      categoryDeleted: 'Category Deleted',
      itemCreated: 'Item Created',
      itemUpdated: 'Item Updated',
      itemDeleted: 'Item Deleted',
      itemGroupCreated: 'Item Group Created',
      itemGroupUpdated: 'Item Group Updated',
      itemGroupDeleted: 'Item Group Deleted',
      inventoryTagCreated: 'Inventory Tag Created',
      inventoryTagUpdated: 'Inventory Tag Updated',
      inventoryTagDeleted: 'Inventory Tag Deleted',
    },
  },
};

export type TranslationsType = typeof en;
